import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BellPlus, X } from "lucide-react";
import { usePersonalReminders } from "@/lib/PersonalRemindersContext";
import { reminderDateToUtc } from "@/lib/personalReminders";
import {
  formatParisDate,
  formatParisTime,
  toParisDateInputValue,
  toParisTimeInputValue,
} from "@/lib/parisDateTime";
export function CreatePersonalReminder({ caseId, title }: { caseId: string; title: string }) {
  const reminders = usePersonalReminders();
  const [open, setOpen] = useState(false),
    [created, setCreated] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  if (!reminders) return null;
  return (
    <>
      <button
        ref={trigger}
        type="button"
        onClick={() => {
          setOpen(true);
          setCreated(false);
        }}
        className="inline-flex shrink-0 items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-800"
      >
        <BellPlus size={16} />
        Créer un rappel
      </button>
      {created && (
        <span role="status" className="text-xs text-green-700">
          Rappel personnel créé
        </span>
      )}
      {open &&
        createPortal(
          <ReminderModal
            caseId={caseId}
            title={title}
            onCreated={() => setCreated(true)}
            onClose={() => {
              setOpen(false);
              trigger.current?.focus();
            }}
          />,
          document.body,
        )}
    </>
  );
}
function ReminderModal({
  caseId,
  title,
  onClose,
  onCreated,
}: {
  caseId: string;
  title: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const reminders = usePersonalReminders()!;
  const initial = new Date(Date.now() + 3600000).toISOString();
  const [action, setAction] = useState<"phone" | "email">("phone"),
    [date, setDate] = useState(toParisDateInputValue(initial)),
    [time, setTime] = useState(toParisTimeInputValue(initial)),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const panel = useRef<HTMLDivElement>(null);
  const myRows = reminders.rows.filter((r) => r.case_id === caseId);
  useEffect(() => {
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
    };
  }, []);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const iso = reminderDateToUtc(date, time);
      await reminders.create(caseId, title, action, iso);
      onCreated();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Impossible de créer le rappel.");
    } finally {
      setBusy(false);
    }
  };
  const cancel = async (id: string) => {
    setBusy(true);
    setError("");
    try {
      await reminders.update(id, { status: "cancelled" });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Réessayez.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/40 p-5"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="personal-reminder-title"
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            if (!busy) onClose();
          }
          if (e.key === "Tab") {
            const items = Array.from(
              panel.current?.querySelectorAll<HTMLElement>(
                "button:not(:disabled),input:not(:disabled),select:not(:disabled)",
              ) || [],
            );
            const first = items[0],
              last = items[items.length - 1];
            if (e.shiftKey && document.activeElement === first) {
              e.preventDefault();
              last?.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
              e.preventDefault();
              first?.focus();
            }
          }
        }}
      >
        <header className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h2 id="personal-reminder-title" className="text-xl font-semibold">
              Créer mon rappel
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Visible uniquement par vous, sur votre compte.
            </p>
            <p className="mt-2 break-words text-sm font-medium">{title}</p>
          </div>
          <button
            autoFocus
            type="button"
            aria-label="Fermer la création du rappel"
            disabled={busy}
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </header>
        <form onSubmit={submit}>
          <fieldset disabled={busy} className="space-y-5 disabled:opacity-60">
            <div>
              <label htmlFor="personal-reminder-action" className="mb-1 block text-sm font-medium">
                Action à effectuer
              </label>
              <select
                id="personal-reminder-action"
                value={action}
                onChange={(e) => setAction(e.target.value as typeof action)}
                className="w-full rounded-lg border p-2.5"
              >
                <option value="phone">Appel téléphonique</option>
                <option value="email">Email</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <label className="text-sm font-medium">
                Date
                <input
                  type="date"
                  required
                  value={date}
                  min={toParisDateInputValue(new Date().toISOString())}
                  onChange={(e) => setDate(e.target.value)}
                  className="mt-1 block w-full rounded-lg border p-2.5"
                />
                {/^\d{4}-\d{2}-\d{2}$/.test(date) && Number.isFinite(Date.parse(date)) && (
                  <span className="mt-1 block text-xs font-normal text-gray-500">
                    {formatParisDate(date + "T12:00:00Z", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                )}
              </label>
              <div>
                <p className="text-sm font-medium">Heure (24 h)</p>
                <div className="mt-1 flex items-center gap-2">
                  <select
                    aria-label="Heures"
                    value={time.split(":")[0]}
                    onChange={(e) => setTime(e.target.value + ":" + time.split(":")[1])}
                    className="min-w-0 flex-1 rounded-lg border p-2.5"
                  >
                    {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map((v) => (
                      <option key={v}>{v}</option>
                    ))}
                  </select>
                  <span>:</span>
                  <select
                    aria-label="Minutes"
                    value={time.split(":")[1]}
                    onChange={(e) => setTime(time.split(":")[0] + ":" + e.target.value)}
                    className="min-w-0 flex-1 rounded-lg border p-2.5"
                  >
                    {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0")).map((v) => (
                      <option key={v}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Heure de Paris, changement d’heure pris en compte. Ce rappel vous avertit dans le CRM
              ; il n’envoie pas d’email au client.
            </p>
            {(error || reminders.error) && (
              <p role="alert" className="text-sm text-red-700">
                {error || reminders.error}
              </p>
            )}
            <button
              type="submit"
              className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 font-medium text-white"
            >
              {busy ? "Enregistrement…" : "Créer mon rappel"}
            </button>
          </fieldset>
        </form>
        {myRows.length > 0 && (
          <section className="mt-6 border-t pt-4">
            <h3 className="text-sm font-semibold">Mes rappels sur ce dossier</h3>
            <ul className="mt-2 space-y-3">
              {myRows.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 text-sm">
                  <span>
                    {r.action === "phone" ? "Appel" : "Email"} · {formatParisDate(r.due_at)} à{" "}
                    {formatParisTime(r.due_at)}
                  </span>
                  <button
                    disabled={busy}
                    onClick={() => void cancel(r.id)}
                    className="text-xs text-gray-500 underline"
                  >
                    Annuler
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
